import { useEffect, useState } from 'react';
import type { ItemForUser, ItemKind, PaymentMethod, SecretPayload } from '@shared/types';
import {
  CATEGORY_SUGGESTIONS,
  EMPTY_SECRET,
  ICON_LABEL,
  ICON_SYMBOLS,
  KIND_HINT,
  KIND_LABEL,
  PAYMENT_LABEL,
  PORTAL_LABEL,
  type IconSymbol,
  type InvoicePortal,
} from '@shared/types';
import {
  CURRENCIES,
  CYCLE_LABEL,
  VAT_RATES,
  formatMoney,
  parseMoneyToCents,
  periodMoney,
  vatLabel,
} from '@shared/money';
import { useVault } from '../lib/vault';
import { api, type ItemInput } from '../lib/api';
import { estimateBits, generatePassword } from '../lib/crypto';
import { CopyButton, ErrorNote, Modal, useAction } from './ui';
import { ItemIcon, SymbolIcon } from './ItemIcon';
import { GuidePicker } from './GuidePicker';

const EMPTY_META: ItemInput = {
  kind: 'shared',
  title: '',
  url: '',
  username: '',
  category: '',
  tags: [],
  status: 'active',
  hasCost: true,
  cycle: 'monthly',
  priceCents: 0,
  currency: 'EUR',
  priceIsPerSeat: false,
  priceIsNet: true,
  vatRatePercent: 19,
  seats: null,
  purchaseDate: null,
  renewalDate: null,
  cancelBeforeDate: null,
  paymentMethod: 'unknown',
  paymentDetail: '',
  billingDay: null,
  ownerUid: null,
  criticality: 'medium',
  onboardingRequired: false,
  rotateOnLeave: true,
  iconSymbol: '',
  iconData: '',
  invoicePortal: 'none',
  invoiceUrl: '',
  guideTitle: '',
  guideUrl: '',
};

const KIND_ORDER: ItemKind[] = ['shared', 'seats', 'license', 'local'];

const PAYMENT_ORDER: PaymentMethod[] = [
  'unknown',
  'amex',
  'mastercard',
  'visa',
  'paypal',
  'sepa',
  'invoice',
  'other',
];

function centsToInput(cents: number): string {
  return cents === 0 ? '' : (cents / 100).toFixed(2).replace('.', ',');
}

/** Beschriftung des Geheimnis-Abschnitts. Bei Plätzen ist es der Verwaltungszugang. */
const SECRET_HEADING: Record<ItemKind, string> = {
  shared: 'Gemeinsames Passwort',
  seats: 'Verwaltungszugang beim Anbieter',
  license: 'Hersteller- oder Händlerkonto',
  local: 'Zugangsdaten',
};

const SECRET_HINT: Record<ItemKind, string> = {
  shared: 'Dieses Passwort benutzen alle Freigegebenen.',
  seats: 'Der Login, mit dem Plätze vergeben werden – etwa die Adobe Admin Console. Optional.',
  license: 'Das Konto, über das gekauft wurde. Die Seriennummern selbst gehören zu den Plätzen. Optional.',
  local: 'WLAN-Schlüssel, Server-Passwort, Gerätezugang. Wer freigegeben ist, sieht ihn.',
};

export function ItemEditor({
  item,
  onClose,
  onSaved,
}: {
  item: ItemForUser | null;
  onClose: () => void;
  onSaved?: (saved: ItemForUser) => void;
}): JSX.Element {
  const { users, me, items, saveItem, readSecret } = useVault();
  const { busy, error, setError, run } = useAction();

  /** Ohne Freigabe lassen sich nur die Stammdaten pflegen - der Geheimtext bleibt unlesbar. */
  const metaOnly = Boolean(item && !item.wrappedKey);

  const [meta, setMeta] = useState<ItemInput>(EMPTY_META);
  const [priceInput, setPriceInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [secret, setSecret] = useState<SecretPayload>(EMPTY_SECRET);
  const [originalPassword, setOriginalPassword] = useState('');
  const [grantUids, setGrantUids] = useState<string[]>([]);
  const [loading, setLoading] = useState(Boolean(item));
  const [showPassword, setShowPassword] = useState(!item);
  const [genLength, setGenLength] = useState(20);
  const [genSymbols, setGenSymbols] = useState(true);
  const [iconBusy, setIconBusy] = useState(false);
  const [iconNote, setIconNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!item) {
        setGrantUids(me ? [me.uid] : []);
        setLoading(false);
        return;
      }
      const {
        id, createdAt, updatedAt, updatedBy, needsRotation, rotationReason, lastRotatedAt,
        secret: _s, accessUids, wrappedKey, assignments, ...rest
      } = item;
      setMeta({ ...EMPTY_META, ...(rest as unknown as ItemInput) });
      setPriceInput(centsToInput(item.priceCents));
      setGrantUids(item.accessUids);
      if (!metaOnly) {
        try {
          const payload = await readSecret(item);
          if (cancelled) return;
          setSecret(payload);
          setOriginalPassword(payload.password);
        } catch (err) {
          if (!cancelled) setError(err instanceof Error ? err.message : String(err));
        }
      }
      if (!cancelled) setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [item, me, metaOnly, readSecret, setError]);

  const patch = <K extends keyof ItemInput>(key: K, value: ItemInput[K]): void =>
    setMeta((prev) => ({ ...prev, [key]: value }));

  const passwordChanged = Boolean(originalPassword) && secret.password !== originalPassword;
  const bits = estimateBits(secret.password);
  const isRecurring = meta.cycle === 'monthly' || meta.cycle === 'quarterly' || meta.cycle === 'weekly';

  const toggleGrant = (uid: string): void =>
    setGrantUids((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));

  const addTag = (): void => {
    const tag = tagInput.trim();
    if (!tag || meta.tags.includes(tag)) return;
    patch('tags', [...meta.tags, tag]);
    setTagInput('');
  };

  /** Kostet der Zugang nichts, werden alle Preisfelder neutralisiert statt nur versteckt. */
  function costFieldsFor(input: ItemInput): ItemInput {
    if (input.hasCost) return input;
    return {
      ...input,
      cycle: 'free',
      priceCents: 0,
      priceIsPerSeat: false,
      vatRatePercent: 0,
      renewalDate: null,
      cancelBeforeDate: null,
      billingDay: null,
      paymentMethod: 'unknown',
      paymentDetail: '',
    };
  }

  async function submit(): Promise<void> {
    if (!meta.title.trim()) {
      setError('Bitte einen Namen für den Dienst angeben.');
      return;
    }
    const finalMeta = costFieldsFor({ ...meta, priceCents: parseMoneyToCents(priceInput) });

    const saved = await run(async () => {
      if (metaOnly && item) {
        const { username: _ignored, ...withoutUsername } = finalMeta;
        await api.patchMeta(item.id, withoutUsername);
        return item;
      }
      return saveItem({
        id: item?.id ?? null,
        meta: finalMeta,
        secret,
        grantUids,
        rotated: passwordChanged,
      });
    });

    if (saved) {
      onSaved?.(saved);
      onClose();
    }
  }

  if (loading) {
    return (
      <Modal title="Wird geladen …" onClose={onClose}>
        <p className="muted small">Eintrag wird entschlüsselt.</p>
      </Modal>
    );
  }

  return (
    <Modal
      wide
      title={item ? meta.title || 'Eintrag bearbeiten' : 'Neuer Eintrag'}
      subtitle={
        metaOnly
          ? 'Du hast keine Freigabe für diesen Eintrag – Passwort und Notizen bleiben verborgen. Stammdaten kannst du trotzdem pflegen.'
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button className="btn-primary" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Speichern …' : 'Speichern'}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      {passwordChanged ? (
        <div className="note note-ok" style={{ marginBottom: 14 }}>
          Das Passwort wurde geändert. Beim Speichern wird der Eintrag neu verschlüsselt und als
          erledigt markiert. Denk daran, es auch beim Anbieter selbst zu ändern.
        </div>
      ) : null}

      {!item ? (
        <>
          <h3 style={{ marginBottom: 10 }}>Was für ein Zugang ist das?</h3>
          <div className="seg" style={{ marginBottom: 6 }}>
            {KIND_ORDER.map((kind) => (
              <button
                key={kind}
                type="button"
                className="seg-option"
                aria-pressed={meta.kind === kind}
                onClick={() => {
                  // Lokale Zugänge haben keinen Vertrag; Plätze und Lizenzen
                  // brauchen selten einen Wechsel des Verwaltungszugangs.
                  setMeta((prev) => ({
                    ...prev,
                    kind,
                    hasCost: kind === 'local' ? false : prev.hasCost,
                    rotateOnLeave: kind === 'shared' || kind === 'local',
                  }));
                }}
              >
                <strong>{KIND_LABEL[kind]}</strong>
                <span className="small muted">{KIND_HINT[kind]}</span>
              </button>
            ))}
          </div>
          <p className="small muted" style={{ marginBottom: 18 }}>
            {meta.kind === 'shared'
              ? 'Ein Passwort für alle – Higgsfield, Artlist, der Instagram-Account.'
              : meta.kind === 'seats'
                ? 'Ein Vertrag, viele eigene Logins – Adobe Creative Cloud, Slack. Die Plätze legst du gleich nach dem Speichern an.'
                : meta.kind === 'license'
                  ? 'Einmal gekauft, mit Seriennummern – DaVinci Resolve, Plugins. Die Seriennummern trägst du gleich nach dem Speichern ein.'
                  : 'Alles im Haus – WLAN, NAS, Server, Alarmanlage. Kein Anbieter, keine Rechnung, aber genauso wichtig.'}
          </p>
        </>
      ) : null}

      <h3 style={{ marginBottom: 10 }}>Dienst</h3>
      <div className="field">
        <label htmlFor="f-title">Name</label>
        <input
          id="f-title"
          value={meta.title}
          onChange={(e) => patch('title', e.target.value)}
          placeholder="z. B. Adobe Creative Cloud"
          autoFocus
        />
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="f-url">Login-Adresse</label>
          <input
            id="f-url"
            value={meta.url}
            onChange={(e) => patch('url', e.target.value)}
            placeholder="https://…"
          />
        </div>
        <div className="field">
          <label htmlFor="f-cat">Kategorie</label>
          <input
            id="f-cat"
            value={meta.category}
            onChange={(e) => patch('category', e.target.value)}
            placeholder="Online-Shop, Versand, Design …"
            list="kategorien"
          />
        </div>
      </div>

      <div className="field">
        <label>Anleitung</label>
        <p className="small muted" style={{ margin: '0 0 6px' }}>
          Wie man diesen Zugang einrichtet oder benutzt. Der Link führt nach awork – wer dort keine
          Rechte hat, sieht das Dokument auch nicht.
        </p>
        <GuidePicker
          title={meta.guideTitle}
          url={meta.guideUrl}
          onChange={(title, url) =>
            setMeta((prev) => ({ ...prev, guideTitle: title, guideUrl: url }))
          }
        />
      </div>

      <div className="field">
        <label>Symbol</label>
        <div className="row" style={{ marginBottom: 10 }}>
          <ItemIcon item={meta} size={40} />
          <button
            className="btn-sm"
            disabled={iconBusy || !meta.url.trim()}
            title={!meta.url.trim() ? 'Dafür braucht es erst eine Login-Adresse' : undefined}
            onClick={async () => {
              setIconBusy(true);
              setIconNote(null);
              try {
                const { dataUri, host } = await api.fetchIcon(meta.url);
                setMeta((prev) => ({ ...prev, iconData: dataUri, iconSymbol: '' }));
                setIconNote(`Symbol von ${host} übernommen.`);
              } catch (err) {
                setIconNote(err instanceof Error ? err.message : String(err));
              } finally {
                setIconBusy(false);
              }
            }}
          >
            {iconBusy ? 'Wird geholt …' : 'Vom Anbieter holen'}
          </button>
          {meta.iconData || meta.iconSymbol ? (
            <button
              className="btn-quiet btn-sm"
              onClick={() => {
                setMeta((prev) => ({ ...prev, iconData: '', iconSymbol: '' }));
                setIconNote(null);
              }}
            >
              Entfernen
            </button>
          ) : null}
        </div>

        <div className="icon-grid">
          {ICON_SYMBOLS.map((name) => (
            <button
              key={name}
              type="button"
              className="icon-pick"
              aria-pressed={meta.iconSymbol === name}
              title={ICON_LABEL[name]}
              onClick={() =>
                setMeta((prev) => ({
                  ...prev,
                  iconSymbol: prev.iconSymbol === name ? '' : name,
                }))
              }
            >
              <SymbolIcon name={name as IconSymbol} />
            </button>
          ))}
        </div>
        <p className="small muted" style={{ margin: '8px 0 0' }}>
          {iconNote ??
            'Ein gewähltes Sinnbild hat Vorrang vor dem Favicon. Geholt wird es einmalig über den Server – dein Browser fragt dabei niemanden.'}
        </p>
      </div>

      <div className="field">
        <label>Schlagwörter</label>
        <div className="row">
          {meta.tags.map((tag) => (
            <span key={tag} className="badge">
              {tag}
              <button
                className="btn-quiet btn-sm"
                style={{ padding: 0, marginLeft: 2 }}
                onClick={() => patch('tags', meta.tags.filter((t) => t !== tag))}
                aria-label={`${tag} entfernen`}
              >
                ✕
              </button>
            </span>
          ))}
          <input
            style={{ maxWidth: 190 }}
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="hinzufügen + Enter"
          />
        </div>
      </div>

      {!metaOnly ? (
        <>
          <h3 style={{ margin: '20px 0 4px' }}>{SECRET_HEADING[meta.kind]}</h3>
          <p className="small muted" style={{ marginBottom: 12 }}>
            {SECRET_HINT[meta.kind]}
          </p>

          <div className="field">
            <label htmlFor="f-user">Login-Name / E-Mail</label>
            <input
              id="f-user"
              value={meta.username}
              onChange={(e) => patch('username', e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="field">
            <label htmlFor="f-pass">Passwort</label>
            <div className="row-tight">
              <input
                id="f-pass"
                type={showPassword ? 'text' : 'password'}
                value={secret.password}
                onChange={(e) => setSecret((s) => ({ ...s, password: e.target.value }))}
                autoComplete="off"
                spellCheck={false}
              />
              <button className="btn-sm" onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? 'Verbergen' : 'Zeigen'}
              </button>
              <CopyButton value={secret.password} />
            </div>
            <div className="row small" style={{ marginTop: 8 }}>
              <button
                className="btn-sm"
                onClick={() => {
                  setSecret((s) => ({
                    ...s,
                    password: generatePassword({
                      length: genLength,
                      upper: true,
                      digits: true,
                      symbols: genSymbols,
                      avoidAmbiguous: true,
                    }),
                  }));
                  setShowPassword(true);
                }}
              >
                Neues Passwort erzeugen
              </button>
              <input
                type="number"
                min={12}
                max={64}
                value={genLength}
                onChange={(e) => setGenLength(Number(e.target.value))}
                style={{ width: 72 }}
                aria-label="Länge"
              />
              <label className="check">
                <input
                  type="checkbox"
                  checked={genSymbols}
                  onChange={(e) => setGenSymbols(e.target.checked)}
                />
                Sonderzeichen
              </label>
              <span className="spacer" />
              {secret.password ? (
                <span className={bits < 60 ? 'badge badge-danger' : bits < 90 ? 'badge badge-warn' : 'badge badge-ok'}>
                  {bits} Bit
                </span>
              ) : null}
            </div>
          </div>

          {meta.kind === 'license' ? (
            <div className="field">
              <label htmlFor="f-license">Haupt-Seriennummer (optional)</label>
              <input
                id="f-license"
                value={secret.licenseKey}
                onChange={(e) => setSecret((s) => ({ ...s, licenseKey: e.target.value }))}
                placeholder="Nur falls es eine übergreifende Nummer gibt"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="f-totp">2FA-Schlüssel (optional)</label>
            <input
              id="f-totp"
              value={secret.totpSecret}
              onChange={(e) => setSecret((s) => ({ ...s, totpSecret: e.target.value }))}
              placeholder="Base32-Geheimnis"
              autoComplete="off"
            />
          </div>

          <div className="field">
            <label htmlFor="f-recovery">Wiederherstellungscodes (optional)</label>
            <textarea
              id="f-recovery"
              value={secret.recoveryCodes}
              onChange={(e) => setSecret((s) => ({ ...s, recoveryCodes: e.target.value }))}
              placeholder="Einer pro Zeile"
            />
          </div>

          <div className="field">
            <label htmlFor="f-notes">Notizen</label>
            <textarea
              id="f-notes"
              value={secret.notes}
              onChange={(e) => setSecret((s) => ({ ...s, notes: e.target.value }))}
              placeholder="Sicherheitsfragen, Kundennummer, Besonderheiten …"
            />
          </div>
        </>
      ) : null}

      <h3 style={{ margin: '22px 0 10px' }}>Kosten</h3>
      <label className="check" style={{ marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={meta.hasCost}
          onChange={(e) => patch('hasCost', e.target.checked)}
        />
        Dieser Zugang kostet Geld
      </label>

      {!meta.hasCost ? (
        <p className="small muted" style={{ marginTop: -6 }}>
          Kein Vertrag, keine Rechnung – etwa ein Shop-Konto oder der DHL-Login. Der Eintrag
          taucht in keiner Kostenrechnung auf.
        </p>
      ) : (
        <>
          <div className="field-row">
            <div className="field">
              <label htmlFor="f-status">Status</label>
              <select
                id="f-status"
                value={meta.status}
                onChange={(e) => patch('status', e.target.value as ItemInput['status'])}
              >
                <option value="active">Aktiv</option>
                <option value="trial">Testphase</option>
                <option value="cancelled">Gekündigt</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-cycle">Abrechnung</label>
              <select
                id="f-cycle"
                value={meta.cycle}
                onChange={(e) => patch('cycle', e.target.value as ItemInput['cycle'])}
              >
                {(['monthly', 'quarterly', 'yearly', 'weekly', 'onetime'] as const).map((value) => (
                  <option key={value} value={value}>
                    {CYCLE_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-price">Preis</label>
              <input
                id="f-price"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </div>
            <div className="field">
              <label htmlFor="f-currency">Währung</label>
              <select
                id="f-currency"
                value={meta.currency}
                onChange={(e) => patch('currency', e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-netgross">Preis ist</label>
              <select
                id="f-netgross"
                value={meta.priceIsNet ? 'net' : 'gross'}
                onChange={(e) => patch('priceIsNet', e.target.value === 'net')}
              >
                <option value="net">netto</option>
                <option value="gross">brutto</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-vat">Umsatzsteuer</label>
              <select
                id="f-vat"
                value={meta.vatRatePercent}
                onChange={(e) => patch('vatRatePercent', Number(e.target.value))}
              >
                {VAT_RATES.map((r) => (
                  <option key={r} value={r}>
                    {vatLabel(r)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {meta.kind !== 'shared' ? (
            <>
              <label className="check" style={{ marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={meta.priceIsPerSeat}
                  onChange={(e) => patch('priceIsPerSeat', e.target.checked)}
                />
                Der Preis gilt je Platz
              </label>
              <div className="field" style={{ maxWidth: 220 }}>
                <label htmlFor="f-seats">
                  {meta.kind === 'license' ? 'Gekaufte Lizenzen' : 'Bezahlte Plätze'}
                </label>
                <input
                  id="f-seats"
                  type="number"
                  min={0}
                  value={meta.seats ?? ''}
                  onChange={(e) =>
                    patch('seats', e.target.value === '' ? null : Number(e.target.value))
                  }
                />
              </div>
            </>
          ) : null}

          <div className="field-row">
            <div className="field">
              <label htmlFor="f-pay">Zahlungsmittel</label>
              <select
                id="f-pay"
                value={meta.paymentMethod}
                onChange={(e) => patch('paymentMethod', e.target.value as PaymentMethod)}
              >
                {PAYMENT_ORDER.map((p) => (
                  <option key={p} value={p}>
                    {PAYMENT_LABEL[p]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="f-paydetail">Näheres dazu</label>
              <input
                id="f-paydetail"
                value={meta.paymentDetail}
                onChange={(e) => patch('paymentDetail', e.target.value)}
                placeholder="••1234, buchhaltung@wach.studio"
              />
            </div>
            {isRecurring ? (
              <div className="field">
                <label htmlFor="f-billingday">Abbuchung am</label>
                <select
                  id="f-billingday"
                  value={meta.billingDay ?? ''}
                  onChange={(e) =>
                    patch('billingDay', e.target.value === '' ? null : Number(e.target.value))
                  }
                >
                  <option value="">– unbekannt –</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}. des Monats
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          <h3 style={{ margin: '20px 0 4px' }}>Rechnung</h3>
          <p className="small muted" style={{ marginBottom: 12 }}>
            Ein Link zum Rechnungsbereich spart bei jeder Buchhaltung das Suchen. Alle Einträge mit
            Link stehen gesammelt unter <em>Rechnungen</em>.
          </p>
          <div className="field-row">
            <div className="field">
              <label htmlFor="f-portal">Woher kommt die Rechnung?</label>
              <select
                id="f-portal"
                value={meta.invoicePortal}
                onChange={(e) => patch('invoicePortal', e.target.value as InvoicePortal)}
              >
                {(Object.keys(PORTAL_LABEL) as InvoicePortal[]).map((k) => (
                  <option key={k} value={k}>
                    {PORTAL_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flexBasis: '100%' }}>
              <label htmlFor="f-invoiceurl">Direkter Link zum Rechnungsbereich</label>
              <input
                id="f-invoiceurl"
                value={meta.invoiceUrl}
                onChange={(e) => patch('invoiceUrl', e.target.value)}
                placeholder="https://…/billing/invoices"
              />
            </div>
          </div>


          <div className="field-row">
            {meta.cycle === 'onetime' ? (
              <div className="field">
                <label htmlFor="f-purchase">Gekauft am</label>
                <input
                  id="f-purchase"
                  type="date"
                  value={meta.purchaseDate ?? ''}
                  onChange={(e) => patch('purchaseDate', e.target.value || null)}
                />
              </div>
            ) : (
              <>
                <div className="field">
                  <label htmlFor="f-renew">Nächste Verlängerung</label>
                  <input
                    id="f-renew"
                    type="date"
                    value={meta.renewalDate ?? ''}
                    onChange={(e) => patch('renewalDate', e.target.value || null)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="f-cancel">Kündigen bis</label>
                  <input
                    id="f-cancel"
                    type="date"
                    value={meta.cancelBeforeDate ?? ''}
                    onChange={(e) => patch('cancelBeforeDate', e.target.value || null)}
                  />
                </div>
              </>
            )}
          </div>

          {priceInput ? (
            (() => {
              const money = periodMoney({ ...meta, priceCents: parseMoneyToCents(priceInput) });
              return (
                <p className="small muted">
                  Je Abrechnung ({CYCLE_LABEL[meta.cycle]}
                  {meta.priceIsPerSeat ? ` , ${meta.seats ?? 1} Plätze` : ''}):{' '}
                  <span className="mono">{formatMoney(money.netCents)}</span> netto
                  {money.vatCents > 0 ? (
                    <>
                      {' '}+ <span className="mono">{formatMoney(money.vatCents)}</span> Steuer
                    </>
                  ) : null}{' '}
                  = <strong className="mono">{formatMoney(money.grossCents)}</strong> brutto.
                </p>
              );
            })()
          ) : null}
        </>
      )}

      <h3 style={{ margin: '22px 0 4px' }}>Ein- und Austritt</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Daraus entstehen die Checklisten, wenn jemand kommt oder geht.
      </p>
      <div className="stack" style={{ marginBottom: 18 }}>
        <label className="check">
          <input
            type="checkbox"
            checked={meta.onboardingRequired}
            onChange={(e) => patch('onboardingRequired', e.target.checked)}
          />
          Neue Leute brauchen diesen Zugang
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={meta.rotateOnLeave}
            onChange={(e) => patch('rotateOnLeave', e.target.checked)}
          />
          Beim Austritt muss das Geheimnis gewechselt werden
        </label>
      </div>
      {meta.rotateOnLeave ? (
        <p className="small muted" style={{ marginTop: -10, marginBottom: 18 }}>
          {meta.kind === 'local'
            ? 'Richtig bei WLAN und Servern: Der Schlüssel muss am Gerät neu gesetzt und im Team verteilt werden.'
            : meta.kind === 'shared'
              ? 'Richtig bei geteilten Logins – wer geht, kennt das Passwort weiterhin.'
              : 'Ungewöhnlich bei Plätzen und Lizenzen: dort wird meist nur der Platz entzogen. Nur anhaken, wenn zusätzlich ein gemeinsamer Verwaltungszugang existiert.'}
        </p>
      ) : null}

      <h3 style={{ margin: '22px 0 10px' }}>Zuständigkeit</h3>
      <div className="field-row">
        <div className="field">
          <label htmlFor="f-owner">Verantwortlich</label>
          <select
            id="f-owner"
            value={meta.ownerUid ?? ''}
            onChange={(e) => patch('ownerUid', e.target.value || null)}
          >
            <option value="">– niemand –</option>
            {users.map((u) => (
              <option key={u.uid} value={u.uid}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="f-crit">Kritikalität</label>
          <select
            id="f-crit"
            value={meta.criticality}
            onChange={(e) => patch('criticality', e.target.value as ItemInput['criticality'])}
          >
            <option value="high">Hoch – Geld, Kundendaten, Domains</option>
            <option value="medium">Mittel – Arbeitswerkzeuge</option>
            <option value="low">Niedrig – unkritisch</option>
          </select>
        </div>
      </div>

      {!metaOnly ? (
        <>
          <h3 style={{ margin: '22px 0 4px' }}>
            {meta.kind === 'shared' ? 'Wer darf das Passwort sehen' : 'Wer verwaltet diesen Eintrag'}
          </h3>
          <p className="small muted" style={{ marginBottom: 12 }}>
            {meta.kind === 'shared'
              ? 'Alle Angehakten können das Passwort entschlüsseln.'
              : 'Diese Personen können Plätze vergeben und den Verwaltungszugang lesen. Wer nur einen Platz hat, gehört nicht hierher.'}
          </p>
          <div className="stack">
            {users
              .filter((u) => u.status === 'active')
              .map((u) => {
                const isMe = u.uid === me?.uid;
                return (
                  <label key={u.uid} className="check">
                    <input
                      type="checkbox"
                      checked={grantUids.includes(u.uid) || isMe}
                      disabled={isMe || !u.publicKey}
                      onChange={() => toggleGrant(u.uid)}
                    />
                    {u.name}
                    <span className="muted small">{u.email}</span>
                    {isMe ? <span className="badge">du</span> : null}
                    {!u.publicKey ? (
                      <span className="badge badge-warn">noch keine Passphrase</span>
                    ) : null}
                  </label>
                );
              })}
          </div>
        </>
      ) : null}

      <datalist id="kategorien">
        {[...new Set([...items.map((i) => i.category).filter(Boolean), ...CATEGORY_SUGGESTIONS])].map(
          (c) => (
            <option key={c} value={c} />
          ),
        )}
      </datalist>
    </Modal>
  );
}
